import type { LockedDictationTarget } from "@/hooks/use-dictation";

export interface DictationTargetRegistration {
    id: string;
    label: string;
    surface?: "chapter" | "assistant";
    isFocused: () => boolean;
    lockTarget: () => LockedDictationTarget | null;
}

const targets = new Map<string, DictationTargetRegistration>();
const listeners = new Set<() => void>();
let activeTargetId: string | null = null;

function notifyTargetChange() {
    for (const listener of listeners) listener();
}

export function registerDictationTarget(
    target: DictationTargetRegistration,
): () => void {
    targets.set(target.id, target);
    if (!activeTargetId) activeTargetId = target.id;
    notifyTargetChange();
    return () => {
        targets.delete(target.id);
        if (activeTargetId === target.id) {
            activeTargetId = targets.keys().next().value ?? null;
        }
        notifyTargetChange();
    };
}

export function activateDictationTarget(id: string) {
    if (!targets.has(id) || activeTargetId === id) return;
    activeTargetId = id;
    notifyTargetChange();
}

export function subscribeToDictationTarget(listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
}

export function getActiveDictationTargetId(): string | null {
    return activeTargetId;
}

export function getDictationTarget(id: string | null): DictationTargetRegistration | null {
    return id ? targets.get(id) ?? null : null;
}

export function getActiveDictationTarget(): DictationTargetRegistration | null {
    return activeTargetId ? (targets.get(activeTargetId) ?? null) : null;
}

export function getFocusedDictationTarget(): DictationTargetRegistration | null {
    const active = getActiveDictationTarget();
    if (active?.isFocused()) return active;
    for (const target of targets.values()) {
        if (target.isFocused()) return target;
    }
    return null;
}
